import React, { useEffect, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { recordAuthSessionActivity } from '@/modules/auth';
import { authUseCases, supabaseAuthSessionPort } from '@/modules/auth';
import { clearLocalStorageCommercialActivations } from '@/modules/commercial';
import { clearLocalStorageOnboardingIntents } from '@/modules/onboarding';
import { authSessionReadService } from '@/modules/user';
import {
  clearAuthenticatedWorkflowIntentCache,
  clearRegistrationResumeChoiceDismissal,
} from '@/modules/workflow-entry';
import { AuthContext, type AppRole, type Profile, type UserRole } from './auth-context';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasInitialized = useRef(false);
  const lastSessionUserId = useRef<string | null>(null);

  // Clear session-scoped workflow mirrors whenever auth identity changes.
  const syncWorkflowSessionState = (nextUserId: string | null) => {
    if (lastSessionUserId.current === nextUserId) {
      return;
    }

    lastSessionUserId.current = nextUserId;
    clearAuthenticatedWorkflowIntentCache();
    clearRegistrationResumeChoiceDismissal();
    clearLocalStorageOnboardingIntents();
    clearLocalStorageCommercialActivations();
  };

  const fetchUserData = async (userId: string) => {
    try {
      const profileData = await authSessionReadService.getProfile(userId);
      if (profileData) {
        setProfile(profileData as unknown as Profile);
      } else {
        setProfile(null);
      }

      const rolesData = await authSessionReadService.getUserRoles(userId);
      if (rolesData) {
        setRoles(rolesData as UserRole[]);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setProfile(null);
      setRoles([]);
    }
  };

  useEffect(() => {
    const syncSession = async (currentSession: Session | null) => {
      syncWorkflowSessionState(currentSession?.user?.id ?? null);
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        // Initial page load must wait for role hydration. Admin routes can
        // redirect to /dashboard if we clear isLoading before roles are fetched.
        await fetchUserData(currentSession.user.id);
      } else {
        setProfile(null);
        setRoles([]);
      }
      setIsLoading(false);
    };

    // Set up auth state listener FIRST
    const unsubscribe = supabaseAuthSessionPort.onAuthStateChange(
      async (_event, currentSession) => {
        syncWorkflowSessionState(currentSession?.user?.id ?? null);
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          // Do not await Supabase reads inside this callback. That can deadlock
          // auth refresh and leave protected screens loading forever.
          setTimeout(() => {
            void fetchUserData(currentSession.user!.id);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
        }

        if (hasInitialized.current) {
          // Only post-bootstrap auth events may flip loading here. During the
          // first load, getSession()+fetchUserData() owns the loading state.
          setIsLoading(false);
        }
      }
    );

    // Then get initial session
    supabaseAuthSessionPort.getSession().then(async (initialSession) => {
      await syncSession(initialSession);
      hasInitialized.current = true;
    });

    return unsubscribe;
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    return authUseCases.signUp({ email, password, fullName });
  };

  const signIn = async (email: string, password: string) => {
    return authUseCases.signIn({ email, password });
  };

  const requestPasswordReset = async (email: string) => {
    return authUseCases.requestPasswordReset({ email });
  };

  const getVerificationRecoveryStatus = async (email: string) => {
    return authUseCases.getVerificationRecoveryStatus({ email });
  };

  const requestVerificationEmail = async (email: string, redirectTo?: string) => {
    return authUseCases.requestVerificationEmail({ email, redirectTo });
  };

  const updatePassword = async (password: string) => {
    return authUseCases.updatePassword({ password });
  };

  const signOut = async () => {
    syncWorkflowSessionState(null);
    await recordAuthSessionActivity('logout');

    await authUseCases.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
  };

  const isSuperAdmin = roles.some((r) => r.role === 'super_admin');
  const isBusinessAdmin = roles.some((r) => r.role === 'business_admin');
  const isStaff = roles.some((r) => r.role === 'staff');

  // Resolve business-owned scope ids separately from facility-owned scope ids.
  const getBusinessIds = (role: AppRole): string[] => {
    return roles.filter((r) => r.role === role && r.business_id).map((r) => r.business_id as string);
  };

  // Resolve the facility scope for the current role without exposing legacy club ids.
  const getFacilityIds = (role: AppRole): string[] => {
    return roles.filter((r) => r.role === role && r.facility_id).map((r) => r.facility_id as string);
  };

  // Check whether the current role is granted for the requested facility.
  const hasFacilityRole = (role: AppRole, facilityId: string): boolean => {
    return roles.some((r) => r.role === role && r.facility_id === facilityId);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        isLoading,
        isSuperAdmin,
        isBusinessAdmin,
        isStaff,
        signUp,
        signIn,
        requestPasswordReset,
        getVerificationRecoveryStatus,
        requestVerificationEmail,
        updatePassword,
        signOut,
        getBusinessIds,
        getFacilityIds,
        hasFacilityRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};


